/**
 * Font Loader
 * 
 * Dynamically loads necessary fonts based on the selected theme.
 * This prevents loading unnecessary fonts and improves performance.
 */

const fontMap = {
  'situation-room-default': 'Open+Sans:wght@300;400;500;600;700',
  'ifrc': 'Open+Sans:wght@300;400;500;600;700', 
  'immap': 'Barlow:wght@300;400;500;600;700',
  'united-nations': 'Lato:wght@300;400;700',
  'nrc': 'Open+Sans:wght@300;400;500;600;700'
};

// Cache to keep track of already loaded fonts
const loadedFonts = new Set();

/**
 * Load a font from Google Fonts
 * @param {string} fontFamily - Font family name with optional weights (e.g., 'Open+Sans:wght@300;400;700')
 */
export function loadFont(fontFamily) {
  if (loadedFonts.has(fontFamily)) {
    return; // Font already loaded
  }

  try {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamily}&display=swap`;
    document.head.appendChild(link);
    loadedFonts.add(fontFamily);
    // Font loaded
  } catch (error) {
    console.error(`Error loading font ${fontFamily}:`, error);
  }
}

/**
 * Load font for a specific theme
 * @param {string} themeName - Theme name (e.g., 'united-nations')
 */
export function loadThemeFont(themeName) {
  const fontFamily = fontMap[themeName];
  if (fontFamily) {
    loadFont(fontFamily);
  } else {
    // Load default font as fallback
    loadFont(fontMap['situation-room-default']);
  }
}

/**
 * Load organization font family dynamically
 * @param {string} fontName - Font family name (e.g., 'Open Sans', 'Roboto')
 */
export function loadOrganizationFont(fontName) {
  // Map font names to Google Fonts URLs with appropriate weights
  const orgFontMap = {
    'Open Sans': 'Open+Sans:wght@300;400;500;600;700',
    'Roboto': 'Roboto:wght@300;400;500;700',
    'Lato': 'Lato:wght@300;400;700',
    'Montserrat': 'Montserrat:wght@300;400;500;600;700',
    'Source Sans Pro': 'Source+Sans+Pro:wght@300;400;600;700',
    'Inter': 'Inter:wght@300;400;500;600;700',
    'Poppins': 'Poppins:wght@300;400;500;600;700',
    'Nunito': 'Nunito:wght@300;400;600;700',
    'Barlow': 'Barlow:wght@300;400;500;600;700',
    'Work Sans': 'Work+Sans:wght@300;400;500;600;700',
    'Fira Sans': 'Fira+Sans:wght@300;400;500;600;700',
    'Merriweather': 'Merriweather:wght@300;400;700',
    'Playfair Display': 'Playfair+Display:wght@400;700',
    'Libre Baskerville': 'Libre+Baskerville:wght@400;700'
  };
  
  const googleFontFamily = orgFontMap[fontName];
  if (googleFontFamily) {
    loadFont(googleFontFamily);
  } else {
    // Fallback to default
    loadFont(orgFontMap['Open Sans']);
  }
}

/**
 * Preload common organization fonts for better UX
 */
export function preloadOrganizationFonts() {
  const commonFonts = ['Open Sans', 'Roboto', 'Lato', 'Montserrat', 'Inter'];
  commonFonts.forEach(font => loadOrganizationFont(font));
}

export default { loadFont, loadThemeFont }; 